import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Label } from 'lib/Label';
import { changeDealData } from 'store/dealManagement/reducer';
import { getProductCategory } from 'store/dealManagement/selectors/getCommonDealDetails';
import { getDealFormReadOnly } from 'store/dealManagement/selectors/getDealFormReadOnly';
import { FilterPlannerProductCategory } from 'components/common/FilterArea/Filters';
import useIsReadOnly from 'components/pages/Planner/useIsReadOnly';
import { Store } from 'components/common/types/Store.types';
import { DealStatus } from 'components/common/types/Deal.types';
import { InputTheme } from 'components/common/types/Input.types';
import { CodeNameModel } from 'components/common/types';
import Tooltip, { TooltipDirection } from 'lib/Tooltip';
import { SelectHandler } from 'lib/AsyncSelect/AsyncSelect.types';
import usePendoTracking from 'customHooks/usePendoTracking';

interface SelectProductCategoryProps {
  placeholder?: string;
  dataTestId?: string;
  withLabel?: boolean;
  isRequired?: boolean;
  isDisabled?: boolean;
}

export const SelectProductCategory = ({
  dataTestId,
  withLabel = false,
  isDisabled = false,
  isRequired = false,
  placeholder = '',
}: SelectProductCategoryProps): React.ReactElement => {
  const meta = useSelector((state: Store) => state.dealManagement.meta);
  const bookingStatusCode = useSelector((state: Store) => state.dealManagement.commonDeal.bookingStatusCode);
  const productCategory = useSelector(getProductCategory);

  const dispatch = useDispatch();
  const readOnly = useSelector(getDealFormReadOnly);
  const { isCampaignReadOnly } = useIsReadOnly();
  const isPendingReservation = bookingStatusCode === DealStatus.PENDING_RESERVATION;
  const { getPendoDataId } = usePendoTracking();

  const sortedProductCategories = useMemo(
    () =>
      meta.productCategoryGroups
        .flatMap((productCategoryGroup) => productCategoryGroup.productCategories!)
        .sort((a, b) => a.name.localeCompare(b.name)),
    [meta.productCategoryGroups],
  );

  const onSelect: SelectHandler<CodeNameModel> = (dropdownName, value: CodeNameModel | null) => {
    dispatch(changeDealData({ [dropdownName]: value }));
  };

  return (
    <>
      {withLabel ? (
        <Label id="product-category" theme={InputTheme.FLAT_GRAY} label="Product category" isRequired={isRequired} />
      ) : null}
      <Tooltip tooltip={productCategory?.name} direction={TooltipDirection.BOTTOM} isHiddenOnFocus>
        <FilterPlannerProductCategory
          dataTestId={dataTestId}
          options={sortedProductCategories}
          selectedValue={productCategory?.code ? { code: productCategory.code, name: productCategory.name } : null}
          onSelect={onSelect}
          isDisabled={isDisabled || (readOnly && !isPendingReservation) || isCampaignReadOnly}
          placeholder={placeholder}
          dataPendoId={getPendoDataId('Product category')}
        />
      </Tooltip>
    </>
  );
};
